import { useLDClient } from "launchdarkly-react-client-sdk";
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAppSelector } from "cortex/ui/src/hooks";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";

type LaunchDarklyAuthState = {
  isLDContextReady: boolean;
};

const LaunchDarklyAuthContext = createContext<LaunchDarklyAuthState>({
  isLDContextReady: false,
});

export function useLaunchDarklyAuthReady() {
  return useContext(LaunchDarklyAuthContext);
}

export function LaunchDarklyAuthSyncProvider({
  children,
}: {
  children: ReactNode;
}) {
  const ldClient = useLDClient();
  const user = useAppSelector(getAuthUser);
  const lastKeyRef = useRef<string | null>(null);
  const [isLDContextReady, setIsLDContextReady] = useState(false);

  const ldContext = useMemo(() => {
    const key = user?.id ?? user?.user_id;
    if (!key) return null;

    return {
      kind: "user" as const,
      key: String(key),
      anonymous: false,
      account_id: user?.account_id ?? undefined,
    };
  }, [user]);

  useEffect(() => {
    if (!ldClient) return;

    // No authenticated user: skip identify entirely.
    if (!ldContext) {
      lastKeyRef.current = null;
      setIsLDContextReady(true);
      return;
    }

    let isActive = true;
    const nextKey = ldContext.key;

    if (lastKeyRef.current === nextKey) {
      setIsLDContextReady(true);
      return () => {
        isActive = false;
      };
    }

    setIsLDContextReady(false);
    lastKeyRef.current = nextKey;

    void ldClient
      .identify(ldContext)
      .then(() => {
        if (!isActive) return;
        setIsLDContextReady(true);
      })
      .catch((error) => {
        if (!isActive) return;
        lastKeyRef.current = null;
        setIsLDContextReady(true);
        console.error("LaunchDarkly identify failed", error);
      });

    return () => {
      isActive = false;
    };
  }, [ldClient, ldContext]);

  return (
    <LaunchDarklyAuthContext.Provider value={{ isLDContextReady }}>
      {children}
    </LaunchDarklyAuthContext.Provider>
  );
}
